import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShieldCheck, Mail, Lock, KeyRound } from "lucide-react";
import { GoogleLogin } from "@react-oauth/google";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { AUTH } from "@/constants/testIds";
import { toast } from "sonner";

export default function Login() {
  const { login, loginTwoFactor, googleLogin } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  // 2FA step
  const [pending2fa, setPending2fa] = useState(null); // { temp_token, email }
  const [otp, setOtp] = useState("");

  const next = loc.state?.from?.pathname || "/app";

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setLoading(true);
    try {
      const result = await login(email, password);
      if (result.requires_2fa) {
        setPending2fa({ temp_token: result.temp_token, email: result.email });
      } else {
        toast.success("Welcome back!");
        nav(next, { replace: true });
      }
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setErr(null); setLoading(true);
    try {
      await loginTwoFactor(pending2fa.temp_token, otp);
      toast.success("Welcome back!");
      nav(next, { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
      // If the temp_token expired, drop back to email/password
      const s = e?.response?.status;
      if (s === 401 && /expired|challenge/i.test(e?.response?.data?.detail || "")) {
        setPending2fa(null); setOtp("");
      }
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const onGoogleSuccess = async (resp) => {
    try {
      await googleLogin({ credential: resp.credential });
      toast.success("Signed in with Google");
      nav(next, { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };
  const onGoogleError = () => setErr(
    "Google sign-in couldn't load. If you see 'Error 400: origin_mismatch', an admin needs to whitelist this URL in Google Cloud Console → Credentials. Meanwhile, sign in with email + password."
  );

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-40" />
        <Link to="/" className="flex items-center gap-2 relative">
          <div className="w-10 h-10 rounded-xl bg-secondary/10 border border-secondary/20 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-secondary" />
          </div>
          <div>
            <div className="font-display font-semibold text-lg">Treasurescare</div>
          </div>
        </Link>
        <div className="relative">
          <div className="overline text-secondary mb-4">Members lounge</div>
          <h2 className="font-display text-4xl font-light leading-tight">
            "The AI validated my claim in 8 seconds. Courier picked up the phone the next morning. Sorted."
          </h2>
          <div className="mt-6 text-sm text-primary-foreground/70">— Priya S., Vault member since 2024</div>
        </div>
        <div className="relative text-xs text-primary-foreground/50">GDPR compliant · UK-regulated</div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-secondary" /></div>
              <span className="font-display font-semibold text-lg">Treasurescare</span>
            </Link>
          </div>

          {!pending2fa && (
            <>
              <div className="overline text-secondary">Welcome back</div>
              <h1 className="mt-2 font-display text-4xl font-light tracking-tight">Sign in to your account.</h1>
              <form onSubmit={submit} className="mt-8 space-y-5">
                <div>
                  <Label htmlFor="email" className="text-xs">Email</Label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email" type="email" required
                      data-testid={AUTH.loginEmail}
                      value={email} onChange={e => setEmail(e.target.value)}
                      className="pl-9"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="password" className="text-xs">Password</Label>
                  <div className="relative mt-1.5">
                    <Lock className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="password" type="password" required
                      data-testid={AUTH.loginPassword}
                      value={password} onChange={e => setPassword(e.target.value)}
                      className="pl-9"
                      placeholder="••••••••"
                    />
                  </div>
                </div>
                {err && <div className="text-sm text-destructive">{err}</div>}
                <Button
                  type="submit"
                  data-testid={AUTH.loginSubmit}
                  disabled={loading}
                  className="w-full bg-primary text-primary-foreground"
                >
                  {loading ? "Signing in…" : "Sign in"}
                </Button>
                <div className="flex justify-end text-xs">
                  <Link to="/forgot-password" className="text-primary hover:underline" data-testid="forgot-password-link">
                    Forgot your password?
                  </Link>
                </div>
                <div className="relative py-2">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
                  <div className="relative flex justify-center text-[10px] overline text-muted-foreground bg-background px-3">or</div>
                </div>
                <div className="flex justify-center" data-testid={AUTH.googleBtn}>
                  <GoogleLogin
                    onSuccess={onGoogleSuccess}
                    onError={onGoogleError}
                    theme="outline"
                    size="large"
                    text="continue_with"
                    shape="rectangular"
                    logo_alignment="left"
                    width="360"
                  />
                </div>
              </form>
              <div className="mt-8 text-sm text-muted-foreground">
                No account yet? <Link to="/signup" className="text-primary font-medium hover:underline">Create one</Link>
              </div>
            </>
          )}

          {pending2fa && (
            <>
              <div className="overline text-secondary">Second step</div>
              <h1 className="mt-2 font-display text-4xl font-light tracking-tight">Verify with your app.</h1>
              <p className="mt-3 text-sm text-muted-foreground">
                Signing in as <span className="text-foreground font-medium">{pending2fa.email}</span>.
                Enter the current 6-digit code from your authenticator app, or one of your one-time backup codes.
              </p>
              <form onSubmit={submitOtp} className="mt-8 space-y-5">
                <div>
                  <Label htmlFor="otp" className="text-xs">Authenticator or backup code</Label>
                  <div className="relative mt-1.5">
                    <KeyRound className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="otp" required autoFocus inputMode="text"
                      data-testid="login-2fa-code"
                      value={otp}
                      onChange={e => setOtp(e.target.value.trim())}
                      className="pl-9 tracking-[0.4em] font-mono"
                      placeholder="123456"
                    />
                  </div>
                </div>
                {err && <div className="text-sm text-destructive">{err}</div>}
                <Button
                  type="submit"
                  disabled={loading || otp.length < 6}
                  data-testid="login-2fa-submit"
                  className="w-full bg-primary text-primary-foreground"
                >
                  {loading ? "Verifying…" : "Verify & continue"}
                </Button>
                <Button
                  type="button" variant="ghost" className="w-full"
                  onClick={() => { setPending2fa(null); setOtp(""); setErr(null); }}
                  data-testid="login-2fa-cancel"
                >
                  Cancel and use a different account
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
